import React, {memo, ReactNode, useCallback, useMemo} from 'react'
import {
  IconContainer,
} from "@biron-data/react-components"
import {EyeIcon, PencilIcon, UsersIcon} from "@heroicons/react/outline";
import Language from "../../../language";
import {DatamodelRoles, Roles, WorkspaceRoles} from "./UserManager.types";
import {TooltipPlacement} from "antd/es/tooltip";
import {isDatamodelRole, isWorkspaceRole} from "./UserManager.utils";
import {TooltipRowWithIcon} from 'themes/SharedStyledComponent';
import styled from "styled-components";
import {wrapWithPopover} from "../../../commons/wrapWithPopover";

interface Props {
  roles: Roles[]
  layout: "row" | "column"
  justifyContent?: "start" | "end"
  tooltipPlacement?: TooltipPlacement
  popover?: ReactNode
}

// eslint-disable-next-line react/display-name
const WorkspaceRights = memo<Props>(({roles,
                                       popover,
                                       layout,
                                       justifyContent,
                                       tooltipPlacement}) => {
  const workspaceRoles = useMemo(() => roles.filter(isWorkspaceRole), [roles])
  const datamodelRoles = useMemo(() => roles.filter(isDatamodelRole), [roles])

  const isDatamodelManager = datamodelRoles?.includes(DatamodelRoles.DATAMODEL_EXPLORER)
  const isDatamodelViewer = datamodelRoles?.includes(DatamodelRoles.DATAMODEL_VIEWER) || isDatamodelManager
  const isReader = workspaceRoles?.includes(WorkspaceRoles.WORKSPACE_READER)
  const isWriter = workspaceRoles?.includes(WorkspaceRoles.WORKSPACE_WRITER)
  const isManager = workspaceRoles?.includes(WorkspaceRoles.WORKSPACE_MANAGER)

  const getBaseColorReader = useCallback((isText: boolean) => {
    const baseColor = isText ? undefined : "var(--medium-grey)"
    return isDatamodelViewer ? baseColor : "var(--light-grey)"
  }, [isDatamodelViewer])
  const getBaseColorWriter = useCallback((isText: boolean) => {
    const baseColor = isText ? undefined : "var(--medium-grey)"
    return isDatamodelManager ? baseColor : "var(--light-grey)"
  }, [isDatamodelManager])

  const resume = useMemo(() => <TooltipRowWithIcon $layout={layout} $justifyContent={justifyContent}>
    {layout === "column" && <TooltipRowWithIcon $layout={"row"}>
        <Category
            color={isReader ? "var(--primary)" : getBaseColorReader(true)}>{Language.get(`admin.workspace.rights.categoryTitle`)}</Category>
    </TooltipRowWithIcon>}
    <TooltipRowWithIcon $layout={"row"} $justifyContent={justifyContent} $color={isReader ? undefined : getBaseColorReader(true)}>
      <IconContainer key="reader" color={isReader ? "var(--primary)" : getBaseColorReader(false)}><EyeIcon/></IconContainer>

      {
        layout === "column" && Language.get(`admin.workspace.rights.${WorkspaceRoles.WORKSPACE_READER}.description.${isReader ? 'can' : 'cannot'}`)
      }
    </TooltipRowWithIcon>
    <TooltipRowWithIcon $layout={"row"} $justifyContent={justifyContent} $color={isWriter ? undefined : getBaseColorWriter(true)}>
      <IconContainer key="writer" color={isWriter ? "var(--primary)" : getBaseColorWriter(false)}><PencilIcon/></IconContainer>
      {
        layout === "column" && Language.get(`admin.workspace.rights.${WorkspaceRoles.WORKSPACE_WRITER}.description.${isWriter ? 'can' : 'cannot'}`)
      }
    </TooltipRowWithIcon>
    <TooltipRowWithIcon $layout={"row"} $justifyContent={justifyContent} $color={isManager ? undefined : getBaseColorWriter(true)}>
      <IconContainer key="manager" color={isManager ? "var(--primary)" : getBaseColorWriter(false)}><UsersIcon/></IconContainer>
      {
        layout === "column" && Language.get(`admin.workspace.rights.${WorkspaceRoles.WORKSPACE_MANAGER}.description.${isManager ? 'can' : 'cannot'}`)
      }
    </TooltipRowWithIcon>
  </TooltipRowWithIcon>, [getBaseColorReader, getBaseColorWriter, isManager, isReader, isWriter, justifyContent, layout])

  return layout === "row" ? wrapWithPopover(
    resume,
    popover,
    tooltipPlacement
  ) : resume
})

export default WorkspaceRights

const Category = styled.div`
    font-weight: 500;
`